import { getFunctions, httpsCallable } from "firebase/functions";
import { getFrontendAiSelection } from "../config/aiConfig";
import { auth } from "../firebase";
import { Asset, FireConfig } from "../types";

type OpenAiMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

type AiResponse = {
  text?: string;
  error?: string;
  body?: string;
};

const aiRequest = async (payload: Record<string, unknown>): Promise<string> => {
  console.log("AI request payload:", payload);
  if (!auth.currentUser) {
    throw new Error("You must be signed in to use AI features.");
  }
  const functions = getFunctions();
  const callable = httpsCallable<Record<string, unknown>, AiResponse>(functions, "callAiApiCallable");
  const result = await callable(payload);
  console.log("AI response data:", result.data);
  return result.data.text ?? "";
};

const requestFeatureAi = async (args: {
  feature: Parameters<typeof getFrontendAiSelection>[0];
  systemInstruction: string;
  prompt: string;
  temperature?: number;
  useSearch?: boolean;
  messages?: OpenAiMessage[];
}): Promise<string> => {
  const { feature, provider, model } = getFrontendAiSelection(args.feature);
  if (provider === "gemini") {
    return aiRequest({
      feature,
      provider,
      model,
      prompt: args.prompt,
      systemInstruction: args.systemInstruction,
      useSearch: args.useSearch,
    });
  }

  return aiRequest({
    feature,
    provider,
    model,
    messages:
      args.messages ?? [
        { role: "system", content: args.systemInstruction },
        { role: "user", content: args.prompt },
      ],
    temperature: args.temperature ?? 0.4,
  });
};

export const analyzePortfolio = async (assets: Asset[]): Promise<string> => {
  try {
    const portfolioSummary = assets.map((a) => ({
      symbol: a.symbol,
      name: a.name,
      allocation: (a.quantity * a.currentPrice).toFixed(2),
      sector: a.sector,
      type: a.type,
      performance: (((a.currentPrice - a.avgPrice) / a.avgPrice) * 100).toFixed(2) + "%",
    }));

    const prompt = `
      You are a senior financial investment analyst. Analyze the following investment portfolio data in JSON format.
      
      Portfolio Data:
      ${JSON.stringify(portfolioSummary, null, 2)}

      Please provide a concise analysis in HTML format (using simple tags like <h3>, <ul>, <li>, <p>, <strong>) that I can render directly inside a div. Do not include markdown backticks or \`\`\`html wrappers.
      
      Structure your response as follows:
      1. <h3>Portfolio Health Score</h3>: A score out of 10 based on diversification and risk.
      2. <h3>Key Strengths</h3>: Bullet points of what is good (e.g., sector diversity, high performers).
      3. <h3>Risk Factors</h3>: Bullet points of potential risks (e.g., over-concentration in one sector, high volatility assets).
      4. <h3>Actionable Recommendations</h3>: 2-3 specific suggestions to improve the portfolio.

      Keep the tone professional, objective, and insightful.
    `;

    const content = await requestFeatureAi({
      feature: "portfolioAnalysis",
      systemInstruction: "You are a senior financial investment analyst.",
      prompt,
      temperature: 0.4,
    });
    return content || "Unable to generate analysis at this time.";
  } catch (error) {
    console.error("AI analysis failed:", error);
    return "<h3>Error</h3><p>Could not analyze portfolio. Please try again later.</p>";
  }
};

export const chatWithAnalyst = async (history: string[], message: string): Promise<string> => {
  try {
    const messages: OpenAiMessage[] = [
      {
        role: "system",
        content: "You are a helpful investment assistant. Keep answers brief and related to finance.",
      },
      ...history.map((entry) => ({ role: "user", content: entry })),
      { role: "user", content: message },
    ];

    return (
      (await requestFeatureAi({
        feature: "analystChat",
        systemInstruction: "You are a helpful investment assistant. Keep answers brief and related to finance.",
        prompt: message,
        messages,
        temperature: 0.4,
      })) || "No response generated."
    );
  } catch (error) {
    console.error(error);
    return "Sorry, I am having trouble connecting to the financial brain right now.";
  }
};

type DividendHistoryPoint = {
  date: string;
  amount: number;
  currency?: string;
};

type DividendPromptHolding = {
  symbol: string;
  name: string;
  recentDividends: DividendHistoryPoint[];
};

type AiQuotePayload = {
  symbol: string;
  price: number | null;
  currency?: string | null;
  asOf?: string | null;
};

const parseAiJson = (text: string): any | null => {
  const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/i) || text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) return null;
  const jsonStr = jsonMatch[1] || jsonMatch[0];
  try {
    return JSON.parse(jsonStr);
  } catch (error) {
    console.warn("Failed to parse AI JSON payload.", error);
    return null;
  }
};

export const getLatestQuoteFromAi = async (symbol: string, asOfDate?: string): Promise<AiQuotePayload | null> => {
  try {
    const today = asOfDate ?? new Date().toISOString().slice(0, 10);
    const prompt = `
      You are a market data assistant. Provide the most recent price for the stock ticker below.

      Ticker: ${symbol}
      Date: ${today}

      Return ONLY a JSON object (no markdown, no comments) with:
      {
        "symbol": "TICKER",
        "price": 0.00,
        "currency": "USD",
        "asOf": "YYYY-MM-DD"
      }

      Use the Date as the reference for "asOf". If the latest price is unknown, set "price" to null and keep the symbol.
    `;

    const text = await requestFeatureAi({
      feature: "latestQuote",
      systemInstruction: "You are a market data assistant.",
      prompt,
      temperature: 0.1,
      useSearch: true,
    });

    const parsed = parseAiJson(text);
    if (!parsed || typeof parsed !== "object") return null;
    const price = Number(parsed.price);
    if (!Number.isFinite(price)) {
      return { symbol: parsed.symbol ?? symbol, price: null, currency: parsed.currency ?? null, asOf: parsed.asOf ?? null };
    }
    return {
      symbol: parsed.symbol ?? symbol,
      price,
      currency: parsed.currency ?? null,
      asOf: parsed.asOf ?? null,
    };
  } catch (error) {
    console.error("Failed to fetch AI quote:", error);
    return null;
  }
};

export const getDividendData = async (holdings: DividendPromptHolding[]): Promise<any[]> => {
  const todayIso = new Date().toISOString().slice(0, 10);
  const targets = holdings.map((holding) => ({
    symbol: holding.symbol,
    name: holding.name,
    recentDividends: holding.recentDividends.slice(0, 6),
  }));
  const prompt = `
      You are helping project dividend schedules. For each holding provide the best estimate for the next dividend event.

      For each stock you are given the ticker, name and the last few dividend payments (date and per share amount). Use that context
      plus current announcements to return the next expected dividend ex-date and pay date with the expected amount per share.
      Today's date is ${todayIso}. Only return the next upcoming dividend within the next 12 months. If none are expected, return null.

      Holdings (compact list):
      ${JSON.stringify(targets, null, 2)}

      Return ONLY a JSON object (no markdown, no comments) keyed by symbol. Each value should be either:
      - an object with the next dividend estimate:
        {
          "exDate": "YYYY-MM-DD",
          "payDate": "YYYY-MM-DD",
          "amount": 0.00,
          "frequency": "Monthly" | "Quarterly" | "Bi-Annually" | "Annually" | "Irregular",
          "currency": "USD" | "GBP"
        }
      - or null if unknown.
    `;

  console.info("[AI] requesting dividend estimates", { holdings: targets.length });
  let text = "";
  try {
    text = await requestFeatureAi({
      feature: "dividendData",
      systemInstruction: "You are a helpful financial data assistant.",
      prompt,
      temperature: 0.4,
      useSearch: true,
    });
  } catch (error) {
    console.error("AI dividend request failed:", error);
    throw error;
  }

    // Extract JSON from markdown code blocks if present
  const parsed = parseAiJson(text);
  if (parsed) {
    if (Array.isArray(parsed)) {
      console.info("[AI] dividend estimates parsed", { rows: parsed.length });
      return parsed;
    }
    if (parsed && typeof parsed === "object") {
      const rows = Object.entries(parsed).flatMap(([symbol, value]) => {
        if (!value || typeof value !== "object") {
          return [];
        }
        return [{ symbol, ...value }];
      });
      console.info("[AI] dividend estimates parsed", { rows: rows.length });
      return rows;
    }
  }

  console.warn("AI dividend response could not be parsed.");
  return [];
};

type IntelligentCsvMapping = {
  date?: string | null;
  action?: string | null;
  symbol?: string | null;
  quantity?: string | null;
  price?: string | null;
  amount?: string | null;
  currency?: string | null;
};

/**
 * Intelligent CSV Mapper
 * Attempts to map unknown broker CSV headers to our internal transaction schema using AI.
 */
export const getIntelligentCSVMapping = async (
  sampleData: string
): Promise<IntelligentCsvMapping | null> => {
  try {
    const prompt = `
      Analyze the following CSV/Text data snippet from a financial broker statement.
      Identify which columns map to these internal fields:
      'date', 'action' (Buy/Sell/Dividend/Deposit/Withdraw), 'symbol', 'quantity', 'price', 'amount' (total value), 'currency'.

      Sample Data:
      ${sampleData}

      Return ONLY a JSON object mapping our field names to the column header name found in the file.
      If a field isn't found, use null.
      Example format: {"date": "Transaction Date", "symbol": "Ticker", ...}
    `;

    const text = await requestFeatureAi({
      feature: "csvMapping",
      systemInstruction: "You are a data mapping assistant.",
      prompt,
      temperature: 0.4,
    });

    const jsonMatch =
      text.match(/```json\n([\s\S]*?)\n```/) || text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const jsonStr = jsonMatch[1] || jsonMatch[0];
    const parsed = JSON.parse(jsonStr) as IntelligentCsvMapping;
    return parsed;
  } catch (error) {
    console.error("AI CSV mapping failed:", error);
    return null;
  }
};

export const analyzeFirePlan = async (
  config: FireConfig,
  summary: { currentPortfolioValue: number; wealthAtRetirement: number; bankruptcyAge?: number; isSustainable: boolean }
): Promise<string> => {
  try {
    const prompt = `You are Firerify’s Financial Independence Coach.

Goal:
Given a user’s financial profile (portfolios/holdings, manual assets, liabilities, pensions/income streams, and FIRE assumptions), produce a practical, UK-aware financial independence plan. This is educational guidance, not financial advice. Be conservative, avoid unrealistic promises, and be explicit about assumptions and uncertainty.

Inputs you will receive as JSON:
{
  "userProfile": { "name": "...", "country": "UK", "currency": "GBP" },
  "fireConfig": {
    "currentAge": number,
    "retirementAge": number,
    "lifeExpectancy": number,
    "fireStyle": "...",
    "riskTolerance": "Low|Medium|High",
    "annualExpenses": number,
    "retirementExpenses": number,
    "withdrawalRate": number,
    "inflationRate": number,
    "investmentReturnRate": number,
    "incomeGrowthRate": number,
    "lifestyleGoals": string,
    "incomeStreams": [
      { "name": string, "amount": number, "type": "Salary|Rental|Pension|Annuity|Side-Hustle|Other", "startAge": number, "endAge": number }
    ],
    "manualAssets": [
      { "name": string, "value": number, "growthRate": number, "type": "Cash|ISA|GIA|Pension|Real Estate|Crypto|Other" }
    ],
    "liabilities": [
      { "name": string, "balance": number, "interestRate": number, "monthlyPayment": number }
    ],
    "snapshots": [
      { "date": "YYYY-MM-DD", "totalNetWorth": number, "liquidAssets": number }
    ]
  },
  "holdings": [
    {
      "symbol": string,
      "name": string,
      "type": "Stock|ETF|Crypto|Cash",
      "sector": string,
      "quantity": number,
      "avgPrice": number,
      "currentPrice": number,
      "baseCurrency": string,
      "quoteCurrency": string,
      "dividendYield": number|null,
      "dailyChangePct": number|null
    }
  ]
}

Rules:
1) Always compute and show:
   - Total net worth (manual assets + portfolio value - liabilities)
   - Liquid assets (Cash/ISA/GIA/Crypto + liquid portfolio portion if provided)
   - FIRE number = retirementExpenses / (withdrawalRate/100)
   - FI progress % = liquidInvestableAssets / FIRE number (explain what you included)
   - Debt summary: total debt, weighted avg interest, monthly payments
2) Pension planning:
   - Identify pension/annuity income streams by type and start age.
   - Explain “bridge period” (from retirementAge until pension-access ages).
   - Suggest actions: consolidate, contribution strategy, and risk alignment (without naming providers).
3) Stocks/ETFs recommendation:
   - Do NOT give hype picks. Prefer diversification.
   - If user riskTolerance is Low → tilt to broad diversified ETFs, bonds/cash buffer.
   - If Medium/High → still anchor with core diversified ETF, then a small satellite allocation.
   - Output up to 5 “watchlist ideas” as categories first (e.g., global equity index ETF, UK equity income ETF, short-duration bond fund, etc.).
   - If user already holds many single stocks, recommend concentration risk controls.
4) Liabilities recommendation:
   - Prioritise high-interest debt payoff vs investing, using a simple decision rule.
   - Suggest emergency fund first (3–6 months) before aggressive investing.
5) Make it actionable:
   - Provide: “Top 5 priorities”, “Next 30 days”, “Next 90 days”.
   - Provide suggested contribution split (ISA vs pension vs debt) consistent with the user’s inputs.
6) Output must be strict JSON (no markdown, no HTML).

Output JSON schema:
{
  "headline": string,
  "keyMetrics": {
    "netWorth": number,
    "portfolioValue": number,
    "manualAssetsTotal": number,
    "liabilitiesTotal": number,
    "liquidAssets": number,
    "annualExpenses": number,
    "retirementExpenses": number,
    "withdrawalRatePct": number,
    "fireNumber": number,
    "fiProgressPct": number
  },
  "riskNotes": [string],
  "observations": [string],
  "recommendations": {
    "topPriorities": [string],
    "next30Days": [string],
    "next90Days": [string],
    "debtPlan": [string],
    "pensionPlan": [string],
    "investmentPlan": {
      "coreApproach": string,
      "suggestedAllocation": [
        { "bucket": "Core diversified", "pct": number, "notes": string },
        { "bucket": "Defensive/buffer", "pct": number, "notes": string },
        { "bucket": "Satellite/tilts", "pct": number, "notes": string }
      ],
      "watchlistIdeas": [
        { "type": "ETF category|Stock theme", "why": string, "risk": string }
      ],
      "rebalancingRule": string
    }
  },
  "assumptionsUsed": {
    "inflationRatePct": number,
    "investmentReturnRatePct": number,
    "incomeGrowthRatePct": number
  },
  "questionsToRefine": [string]
}

Now analyse this input:
${JSON.stringify(
  {
    userProfile: { name: 'User', country: 'UK', currency: 'GBP' },
    fireConfig: config,
    holdings: [],
    projectionSummary: summary,
  },
  null,
  2
)}

Return strict JSON only.`;

    const content = await requestFeatureAi({
      feature: "firePlan",
      systemInstruction: "You are Firerify’s Financial Independence Coach.",
      prompt,
      temperature: 0.4,
    });
    if (!content) return "Unable to generate analysis at this time.";

    const jsonMatch = content.match(/```json\n([\s\S]*?)\n```/) || content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return content;

    const parsed = JSON.parse(jsonMatch[1] || jsonMatch[0]) as {
      headline?: string;
      keyMetrics?: Record<string, number>;
      riskNotes?: string[];
      observations?: string[];
      recommendations?: {
        topPriorities?: string[];
        next30Days?: string[];
        next90Days?: string[];
      };
      questionsToRefine?: string[];
    };

    const list = (items?: string[]) =>
      items && items.length > 0 ? `<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>` : "<p>None provided.</p>";

    const km = parsed.keyMetrics || {};
    return `
      <h3>${parsed.headline || "FIRE Plan Analysis"}</h3>
      <h4>Key Metrics</h4>
      <ul>
        <li>Net Worth: ${km.netWorth ?? "N/A"}</li>
        <li>Portfolio Value: ${km.portfolioValue ?? "N/A"}</li>
        <li>Liquid Assets: ${km.liquidAssets ?? "N/A"}</li>
        <li>FIRE Number: ${km.fireNumber ?? "N/A"}</li>
        <li>FI Progress: ${km.fiProgressPct ?? "N/A"}%</li>
      </ul>
      <h4>Risk Notes</h4>
      ${list(parsed.riskNotes)}
      <h4>Observations</h4>
      ${list(parsed.observations)}
      <h4>Top 5 Priorities</h4>
      ${list(parsed.recommendations?.topPriorities)}
      <h4>Next 30 Days</h4>
      ${list(parsed.recommendations?.next30Days)}
      <h4>Next 90 Days</h4>
      ${list(parsed.recommendations?.next90Days)}
      <h4>Questions to Refine</h4>
      ${list(parsed.questionsToRefine)}
    `;
  } catch (error) {
    console.error("AI FIRE analysis failed:", error);
    return "<h3>Error</h3><p>Could not analyze the plan. Please try again later.</p>";
  }
};
